import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkle, ArrowRight, X, Users } from '@phosphor-icons/react';

/**
 * Global upgrade prompt modal.
 *
 * Listens for the `c2p:upgrade-required` CustomEvent dispatched by the
 * API interceptor in lib/api.js when the server returns:
 *   - HTTP 402 + code "PLAN_UPGRADE_REQUIRED"  (feature gated behind plan)
 *   - HTTP 402 + code "MAX_USERS_REACHED"       (user-seat cap hit)
 *
 * Renders as a centred modal overlay — user stays on the current page.
 * Mount exactly once in App.js (outside <Routes>).
 */

const COPY = {
  PLAN_UPGRADE_REQUIRED: {
    icon: Lock,
    iconBg: 'from-[#6B4D5F] to-[#3B2535]',
    iconColor: 'text-amber-300',
    badge: 'Plan upgrade needed',
    title: 'This feature needs a higher plan',
    body: (d) =>
      d?.detail ||
      'Unlock this feature by upgrading to Growth or Pro. You can explore all plan details on the Billing page.',
    cta: 'See Growth & Pro plans',
  },
  MAX_USERS_REACHED: {
    icon: Users,
    iconBg: 'from-[#1A3A5C] to-[#0E2440]',
    iconColor: 'text-sky-300',
    badge: 'User seat limit reached',
    title: 'Your plan\u2019s user limit is full',
    body: (d) =>
      d?.detail ||
      `Your current plan allows up to ${d?.max_users ?? '?'} users. Upgrade your plan to invite more team members.`,
    cta: 'Upgrade plan',
  },
};

const DEFAULT = COPY.PLAN_UPGRADE_REQUIRED;

export default function UpgradeModal() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState(null);

  const handleEvent = useCallback((e) => {
    setPayload(e.detail || {});
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener('c2p:upgrade-required', handleEvent);
    return () => window.removeEventListener('c2p:upgrade-required', handleEvent);
  }, [handleEvent]);

  if (!open) return null;

  const code = payload?.code || 'PLAN_UPGRADE_REQUIRED';
  const copy = COPY[code] || DEFAULT;
  const Icon = copy.icon;
  const currentPlan = payload?.current_plan;

  function handleCta() {
    setOpen(false);
    navigate('/billing');
  }

  return (
    <div
      data-testid="upgrade-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
    >
      {/* Backdrop — pointer-events-none so clicks pass through to the overlay handler */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-none" />

      {/* Card */}
      <div
        data-testid="upgrade-modal"
        className="relative z-10 w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
      >
        {/* Dark gradient header */}
        <div className="relative bg-gradient-to-br from-[#2B1E25] via-[#3A2A33] to-[#6B4D5F] px-7 pt-7 pb-6">
          {/* Glow blobs */}
          <div aria-hidden className="absolute -right-8 -top-8 h-36 w-36 rounded-full bg-amber-400/25 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -left-8 -bottom-8 h-36 w-36 rounded-full bg-rose-400/15 blur-3xl pointer-events-none" />

          {/* Close */}
          <button
            data-testid="upgrade-modal-close"
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X size={14} weight="bold" />
          </button>

          {/* Icon + badge */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${copy.iconBg} flex items-center justify-center shadow-lg flex-shrink-0`}>
              <Icon size={22} weight="duotone" className={copy.iconColor} />
            </div>
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-amber-300/90">
              {copy.badge}
            </span>
          </div>

          <h2
            id="upgrade-modal-title"
            data-testid="upgrade-modal-title"
            className="text-xl font-extrabold text-white leading-snug"
          >
            {copy.title}
          </h2>
          <p
            data-testid="upgrade-modal-body"
            className="text-sm text-white/75 mt-2 leading-relaxed"
          >
            {copy.body(payload)}
          </p>
        </div>

        {/* White footer */}
        <div className="bg-white px-7 py-5 flex flex-col gap-3">
          {/* Feature hints */}
          <ul className="space-y-1.5 mb-1">
            {['More users & factories', 'Advanced costing & reports', 'Contractor portal access', 'Priority support'].map((line) => (
              <li key={line} className="flex items-center gap-2 text-xs text-[#5A4F54]">
                <Sparkle size={11} weight="fill" className="text-[#6B4D5F] shrink-0" />
                {line}
              </li>
            ))}
          </ul>

          <button
            data-testid="upgrade-modal-cta"
            onClick={handleCta}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-[#6B4D5F] to-[#5A3D4E] hover:from-[#7C5970] hover:to-[#6B4D5F] text-white font-bold text-sm transition-all shadow-md active:scale-[0.98]"
          >
            {copy.cta} <ArrowRight size={14} weight="bold" />
          </button>

          <div className="flex items-center justify-between">
            {currentPlan && (
              <p className="text-[11px] text-[#9A8B90]">
                Current plan: <span className="font-semibold capitalize text-[#6B4D5F]">{currentPlan}</span>
              </p>
            )}
            <button
              data-testid="upgrade-modal-dismiss"
              onClick={() => setOpen(false)}
              className="text-[11px] text-[#9A8B90] hover:text-[#6B4D5F] transition-colors ml-auto"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
